import { useEffect, useState } from "react";
import { api, absUrl } from "@/lib/api";
import { Skull, Share2, Crown } from "lucide-react";
import { toast } from "sonner";

/**
 * Bounty Board / "Wanted" tile for the Login page.
 * Pulls the top-killed enemies from run_history and rotates through them
 * every 8s to make the game feel alive before the player signs in.
 * ONE bounty is marked "BOUNTY OF THE WEEK" — deterministically picked from
 * ISO year+week seed so every visitor sees the same featured hunt all week,
 * with a shareable "SHARE HUNT" button for viral acquisition.
 */
export default function BountyBoard() {
  const [bounties, setBounties] = useState([]);
  const [weekly, setWeekly]     = useState(null);
  const [idx, setIdx] = useState(0);
  const [fading, setFading] = useState(false);
  const [fetched, setFetched] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await api.get("/bounty/featured?limit=6");
        if (alive && res?.bounties?.length) {
          setBounties(res.bounties);
          if (res.weekly_featured) setWeekly(res.weekly_featured);
        }
      } catch {
        /* silently degrade — bounty board is decorative */
      } finally {
        if (alive) setFetched(true);
      }
    })();
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (bounties.length < 2) return;
    const t = setInterval(() => {
      setFading(true);
      setTimeout(() => {
        setIdx((i) => (i + 1) % bounties.length);
        setFading(false);
      }, 360);
    }, 8000);
    return () => clearInterval(t);
  }, [bounties.length]);

  // Cold-start skeleton: same panel chrome + rotating sigil while the first fetch is in flight.
  // Prevents the tile from popping in and mirrors the in-game "SUMMONING" ceremony.
  if (!bounties.length) {
    if (fetched) return null; // fetch finished with zero results — hide entirely
    return (
      <div
        data-testid="bounty-board-skeleton"
        className="panel p-4 relative overflow-hidden"
        style={{
          background:
            "linear-gradient(180deg, rgba(20,10,6,0.85) 0%, rgba(8,4,2,0.92) 100%)",
          borderColor: "rgba(212,175,55,0.35)",
          minHeight: 128,
        }}
      >
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Skull size={12} className="text-[#c0392b] opacity-60" />
            <span className="text-[10px] tracking-[0.45em] gold-text font-heading opacity-70">
              WANTED · DEAD
            </span>
          </div>
          <span className="text-[9px] tracking-[0.3em] ash-text opacity-60">
            BOUNTY BOARD
          </span>
        </div>
        <div className="flex items-center gap-4">
          <div
            className="relative flex-shrink-0 rounded-sm overflow-hidden flex items-center justify-center"
            style={{
              width: 72,
              height: 96,
              background:
                "radial-gradient(ellipse at 50% 40%, rgba(58,26,10,0.55) 0%, rgba(10,5,3,1) 70%)",
              boxShadow:
                "0 0 0 1px rgba(212,175,55,0.4), inset 0 0 24px rgba(0,0,0,0.7)",
            }}
          >
            <div
              className="portrait-sigil"
              style={{ width: 56, height: 56 }}
              aria-hidden
            />
          </div>
          <div className="min-w-0 flex-1">
            <div className="h-4 w-2/3 rounded-sm skeleton opacity-40" />
            <div className="mt-2 h-px bg-gradient-to-r from-[#d4af37]/30 via-[#d4af37]/10 to-transparent" />
            <div className="mt-3 flex items-baseline gap-2">
              <span className="text-[10px] tracking-[0.3em] ash-text italic opacity-70">
                SCRYING THE BOUNTY BOARD...
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  }
  const b = bounties[idx];
  const isWeekly = !!(weekly && b && (b.slug === weekly.slug));

  const shareHunt = async () => {
    const target = weekly || b;
    if (!target) return;
    const url = `${window.location.origin}/hunt/${encodeURIComponent(target.slug)}?utm_source=hunt&utm_medium=share&utm_campaign=${encodeURIComponent(target.slug)}`;
    const text = `⚔️ BOUNTY OF THE WEEK · ${target.name} — ${target.kills.toLocaleString()} souls claimed. Hunt with me in Champions of the Dawn Vanguard.`;
    // Try native share first (mobile), else copy to clipboard.
    if (navigator.share) {
      try {
        await navigator.share({ title: `Hunt: ${target.name}`, text, url });
        return;
      } catch {
        // user cancelled — fall through to copy
      }
    }
    try {
      await navigator.clipboard.writeText(`${text}\n${url}`);
      toast.success("Hunt link copied. Share it with your allies.");
    } catch {
      // last-resort: X intent
      const intent = `https://x.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`;
      window.open(intent, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <div
      data-testid="bounty-board"
      className="panel p-4 relative overflow-hidden"
      style={{
        background:
          "linear-gradient(180deg, rgba(20,10,6,0.85) 0%, rgba(8,4,2,0.92) 100%)",
        borderColor: isWeekly ? "rgba(212,175,55,0.75)" : "rgba(212,175,55,0.35)",
        boxShadow: isWeekly ? "0 0 24px rgba(212,175,55,0.15), inset 0 0 32px rgba(212,175,55,0.05)" : undefined,
      }}
    >
      {/* Weekly ribbon — top-right corner */}
      {isWeekly && (
        <div
          data-testid="bounty-weekly-ribbon"
          className="absolute top-0 right-0 pl-3 pr-2 py-1 flex items-center gap-1.5"
          style={{
            background: "linear-gradient(90deg, transparent, rgba(212,175,55,0.9))",
            transform: "translateY(-1px)",
          }}
        >
          <Crown size={10} className="text-[#1a0f08]" />
          <span className="text-[9px] tracking-[0.35em] font-heading text-[#1a0f08]">
            BOUNTY · OF · THE · WEEK
          </span>
        </div>
      )}
      {/* Aged parchment noise */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none opacity-[0.08] mix-blend-overlay"
        style={{
          backgroundImage:
            "radial-gradient(circle at 20% 20%, #d4af37 0%, transparent 40%), radial-gradient(circle at 80% 70%, #6b3410 0%, transparent 45%)",
        }}
      />
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Skull size={12} className="text-[#c0392b]" />
          <span
            className="text-[10px] tracking-[0.45em] gold-text font-heading"
            style={{ textShadow: "0 0 8px rgba(212,175,55,0.35)" }}
          >
            WANTED · DEAD
          </span>
        </div>
        <span className="text-[9px] tracking-[0.3em] ash-text">
          BOUNTY BOARD
        </span>
      </div>

      <div
        className={`flex items-center gap-4 transition-opacity duration-300 ${
          fading ? "opacity-0" : "opacity-100"
        }`}
        data-testid="bounty-featured"
      >
        <div
          className="relative flex-shrink-0 rounded-sm overflow-hidden"
          style={{
            width: 72,
            height: 96,
            boxShadow:
              "0 0 0 1px rgba(212,175,55,0.4), inset 0 0 24px rgba(0,0,0,0.7)",
          }}
        >
          <img
            src={absUrl(b.portrait)}
            alt={b.name}
            className="w-full h-full object-cover"
            style={{ filter: "sepia(0.35) contrast(1.05) saturate(0.85)" }}
            onError={(e) => { e.currentTarget.style.opacity = 0; }}
          />
          {b.is_boss && (
            <div className="absolute top-1 right-1 text-[8px] px-1 py-0.5 bg-[#c0392b] text-[#f0e6d2] tracking-widest rounded-sm">
              BOSS
            </div>
          )}
        </div>

        <div className="min-w-0 flex-1">
          <div
            className="font-heading text-lg text-[#f0e6d2] truncate"
            data-testid="bounty-name"
            title={b.name}
          >
            {b.name}
          </div>
          <div className="mt-1 h-px bg-gradient-to-r from-[#d4af37]/50 via-[#d4af37]/20 to-transparent" />
          <div className="mt-2 flex items-baseline gap-2">
            <span
              className="font-heading text-2xl gold-text"
              data-testid="bounty-kills"
            >
              {b.kills.toLocaleString()}
            </span>
            <span className="text-[10px] tracking-[0.3em] ash-text">
              {b.kills === 0
                ? "AT LARGE · UNCLAIMED"
                : b.kills === 1
                ? "SOUL CLAIMED"
                : "SOULS CLAIMED"}
            </span>
          </div>
          <p className="mt-2 text-[11px] ash-text italic font-narrative leading-snug">
            {b.kills === 0
              ? "First blood awaits. Be the champion who strikes this foe down."
              : "Slain be this foe — and the Ember shall remember your name."}
          </p>
        </div>
      </div>

      {/* Share Hunt CTA — always available, extra prominent on weekly bounty */}
      <button
        type="button"
        onClick={shareHunt}
        className="mt-3 w-full flex items-center justify-center gap-2 py-2 px-3 border border-[#d4af37]/40 hover:border-[#d4af37] hover:bg-[#d4af37]/5 transition-colors group"
        data-testid="bounty-share-hunt"
        style={{
          background: isWeekly ? "linear-gradient(90deg, rgba(212,175,55,0.08), transparent)" : undefined,
        }}
      >
        <Share2 size={11} className="ash-text group-hover:text-[#d4af37] transition-colors" />
        <span className="text-[10px] tracking-[0.35em] ash-text group-hover:gold-text transition-colors font-heading">
          {isWeekly ? "SHARE THIS HUNT" : "INVITE A HUNTER"}
        </span>
      </button>
    </div>
  );
}
